import React from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import AddIcon from '@mui/icons-material/Add';
import { Alert, Box, Button } from '@mui/material';
import { useFieldArray, useWatch } from 'react-hook-form';
import AccordionCard from '../../../Shared/Components/AccordionCard/AccordionCard';
import InvoiceItemRow from './InvoiceItemRow';
import type { InvoiceItemsSectionProps } from './InvoiceItemsSection.types';
import { useGetInvoiceReservationWithAssociationsByIdQuery } from '../../Service/reservationsLookupApi';
import { VatRateType, InvoiceScheme, InvoiceType } from '../../Types/invoicesTypes';
import type { CreateInvoiceForm, CreateInvoiceItemForm } from '../../Types/createInvoiceFormTypes';
import {
  getAllowedVatTypesForInvoice,
  getDefaultVatExemptNote,
  getPreferredVatTypeForInvoice,
  normalizeVatType,
} from '../../utils/vatMappings';
import { formatInvoiceItemName } from '../../utils/invoiceHelpers';
import { normalizeInvoiceScheme, normalizeInvoiceType } from '../../utils/invoiceEnumNormalization';
import { invoiceLogger } from '../../utils/invoiceLogger';

const InvoiceItemsSection: React.FC<InvoiceItemsSectionProps> = ({
  control,
  setValue,
  errors,
  reservationId,
  scheme,
  invoiceType,
  isCorrectionInvoice = false,
  disableReservationPrefill = false,
  onItemAdded,
}) => {
  // useFieldArray dla dynamicznej listy items
  const { fields, append, remove, update, replace } = useFieldArray<CreateInvoiceForm, 'items', 'fieldKey'>({
    control,
    name: 'items',
    keyName: 'fieldKey',
  });

  // ✅ Store stable reference to update (prevents handleItemChange re-creation)
  const updateRef = React.useRef(update);
  React.useEffect(() => {
    updateRef.current = update;
  }, [update]);

  // Watch items to get current form state (wrapped in useMemo for stable reference)
  const watchedItems = useWatch({ control, name: 'items' });
  const items = React.useMemo(() => watchedItems || [], [watchedItems]);
  const itemsRef = React.useRef<CreateInvoiceItemForm[]>(items as CreateInvoiceItemForm[]);

  React.useEffect(() => {
    itemsRef.current = (items as CreateInvoiceItemForm[]) ?? [];
  }, [items]);

  React.useEffect(() => {
    if (fields.length > 0) {
      return;
    }

    if (!Array.isArray(items) || items.length === 0) {
      return;
    }

    replace(items as CreateInvoiceItemForm[]);
  }, [fields.length, items, replace]);

  // ✅ Watch paidAmount for Prepayment invoices
  const paidAmount = useWatch({ control, name: 'paidAmount' }) as number | undefined;

  // ✅ Track previous vatType for each item (survives re-renders)
  const prevVatTypesRef = React.useRef<Map<number, VatRateType>>(new Map());

  // ✅ Track pending updates to avoid stale data from useWatch
  const pendingUpdatesRef = React.useRef<Map<number, Partial<CreateInvoiceItemForm>>>(new Map());

  // Prefill from reservation
  const { data: reservationDetails } = useGetInvoiceReservationWithAssociationsByIdQuery(
    reservationId!,
    {
      skip: !reservationId,
    }
  );

  const normalizedScheme = React.useMemo(() => normalizeInvoiceScheme(scheme), [scheme]);
  const normalizedInvoiceType = React.useMemo(
    () => normalizeInvoiceType(invoiceType),
    [invoiceType]
  );
  const preferredVatType = React.useMemo(
    () => getPreferredVatTypeForInvoice(normalizedScheme, normalizedInvoiceType),
    [normalizedScheme, normalizedInvoiceType]
  );

  // Enforce valid VAT types whenever scheme/type context changes or items first appear.
  // This covers reservation-prefilled rows that may be appended after the initial effect run.
  React.useEffect(() => {
    const currentItems = itemsRef.current;

    if (!Array.isArray(currentItems) || currentItems.length === 0) {
      return;
    }

    const allowedVatTypes = getAllowedVatTypesForInvoice(normalizedScheme, normalizedInvoiceType);

    currentItems.forEach((item, index) => {
      const currentVatType = normalizeVatType(item?.vatType as any);
      const isAllowed = allowedVatTypes.includes(currentVatType);

      invoiceLogger.debug('[InvoiceItemsSection] VAT_REPLACEMENT_DECISION', {
        index,
        currentVatType,
        rawVatType: item?.vatType,
        isAllowed,
        preferredVatType,
        normalizedScheme,
        normalizedInvoiceType,
        allowedVatTypes,
      });

      if (isAllowed) {
        return;
      }

      const replacementNote = getDefaultVatExemptNote(preferredVatType, normalizedInvoiceType);

      invoiceLogger.warn(
        '[InvoiceItemsSection] Replacing invalid vatType after scheme/type change',
        {
          index,
          previousVatType: item?.vatType,
          replacementVatType: preferredVatType,
          normalizedScheme,
          normalizedInvoiceType,
          allowedVatTypes,
        }
      );

      setValue(`items.${index}.vatType` as any, preferredVatType, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: false,
      });

      invoiceLogger.warn('[InvoiceItemsSection] VAT_REPLACEMENT_APPLIED', {
        index,
        previousVatType: item?.vatType,
        nextVatType: preferredVatType,
        normalizedScheme,
        normalizedInvoiceType,
      });

      if (replacementNote && item?.vatExemptNote !== replacementNote) {
        setValue(`items.${index}.vatExemptNote` as any, replacementNote, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        });
      }
    });
  }, [items.length, normalizedScheme, normalizedInvoiceType, preferredVatType, setValue]);

  // Debug: Log raw backend response
  React.useEffect(() => {
    if (reservationDetails) {
      invoiceLogger.debug('[InvoiceItemsSection] Backend response (reservationDetails):', {
        id: reservationDetails.id,
        totalPrice: reservationDetails.totalPrice,
        unitPrice: reservationDetails.unitPrice,
        numberOfParticipants: reservationDetails.numberOfParticipants,
        applicant: {
          firstName: reservationDetails.applicant?.firstName,
          lastName: reservationDetails.applicant?.lastName,
        },
        booking: {
          id: reservationDetails.booking?.id,
          offerId: reservationDetails.booking?.offerId,
          offerName: reservationDetails.booking?.offerName,
          bookingDateFrom: reservationDetails.booking?.bookingDateFrom,
          bookingDateTo: reservationDetails.booking?.bookingDateTo,
        },
        fullObject: reservationDetails,
      });
      invoiceLogger.debug(
        '[InvoiceItemsSection] formatInvoiceItemName result:',
        formatInvoiceItemName(reservationDetails)
      );
    }
  }, [reservationDetails]);

  // Persist prefill into RHF state once per reservation
  const appliedForReservationRef = React.useRef<number | null>(null);
  const prefilledItemNameRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const totalPrice = reservationDetails?.totalPrice;

    invoiceLogger.debug('[InvoiceItemsSection] 🔍 Prefill evaluation START:', {
      reservationId,
      'reservationDetails exists': !!reservationDetails,
      totalPrice,
      'totalPrice type': typeof totalPrice,
      'totalPrice != null': totalPrice != null,
      'appliedForReservationRef.current': appliedForReservationRef.current,
      'fields.length': fields.length,
      isCorrectionInvoice,
    });

    // Only apply if:
    // 1. We have a reservationId and totalPrice
    // 2. We haven't applied this reservationId yet
    // 3. No items exist yet (fields.length === 0)
    // 4. NOT a correction invoice (items come from base invoice, not reservation)
    const shouldApply =
      !!reservationId &&
      totalPrice != null &&
      appliedForReservationRef.current !== reservationId &&
      fields.length === 0 &&
      !isCorrectionInvoice &&
      !disableReservationPrefill &&
      (!Array.isArray(items) || items.length === 0);

    invoiceLogger.debug('[InvoiceItemsSection] Prefill check:', {
      reservationId,
      totalPrice,
      appliedFor: appliedForReservationRef.current,
      fieldsLength: fields.length,
      isCorrectionInvoice,
      disableReservationPrefill,
      shouldApply,
      'Check 1 - !!reservationId': !!reservationId,
      'Check 2 - totalPrice != null': totalPrice != null,
      'Check 3 - not applied yet': appliedForReservationRef.current !== reservationId,
      'Check 4 - fields.length === 0': fields.length === 0,
      'Check 5 - !isCorrectionInvoice': !isCorrectionInvoice,
      'Check 6 - !disableReservationPrefill': !disableReservationPrefill,
      'Check 7 - items.length === 0': !Array.isArray(items) || items.length === 0,
    });

    if (shouldApply) {
      invoiceLogger.debug('[InvoiceItemsSection] Applying prefill with totalPrice:', totalPrice);
      invoiceLogger.debug('[InvoiceItemsSection] Current normalized scheme:', normalizedScheme);
      invoiceLogger.debug(
        '[InvoiceItemsSection] Current normalized invoiceType:',
        normalizedInvoiceType,
        'type:',
        typeof normalizedInvoiceType
      );
      invoiceLogger.debug('[InvoiceItemsSection] InvoiceType.Refund value:', InvoiceType.Refund);
      invoiceLogger.debug('[InvoiceItemsSection] Comparison:', {
        'normalizedInvoiceType === InvoiceType.Refund':
          normalizedInvoiceType === InvoiceType.Refund,
        normalizedInvoiceType,
      });

      // Determine vatType based on invoice type and scheme
      // Priority: Refund → VatNP (PFRON)
      //           TravelMargin scheme → VatMargin
      //           Default → Vat23
      const isRefund = normalizedInvoiceType === InvoiceType.Refund;
      const isTravelMargin = normalizedScheme === InvoiceScheme.TravelMargin;

      const defaultVatType = isRefund
        ? VatRateType.VatNP
        : isTravelMargin
          ? VatRateType.VatMargin
          : VatRateType.Vat23;

      // Get appropriate legal note for the vatType (dynamic based on vatType and invoiceType)
      const defaultNote = getDefaultVatExemptNote(defaultVatType, normalizedInvoiceType);

      invoiceLogger.debug('[InvoiceItemsSection] VAT type selection:', {
        isRefund,
        isTravelMargin,
        isPrepayment: normalizedInvoiceType === InvoiceType.Prepayment,
        selectedVatType: defaultVatType,
        noteLength: defaultNote?.length || 0,
        paidAmount,
      });

      // ✅ FIX: For Prepayment invoices, use paidAmount as unitGross (not totalPrice as unitNet)
      // Prepayment: user pays a deposit (gross amount), backend calculates net from gross
      // Standard: use totalPrice from reservation as unitNet
      const isPrepayment = normalizedInvoiceType === InvoiceType.Prepayment;

      // Format item name: "Zaliczka - [offer name]" for Prepayment, "[offer name]" for others
      const baseItemName = formatInvoiceItemName(reservationDetails);
      const itemName = isPrepayment ? `Zaliczka - ${baseItemName}` : baseItemName;

      const itemToAdd = isPrepayment
        ? {
            name: itemName,
            description: '',
            quantity: 1,
            unitNet: 0, // ✅ Backend will calculate from unitGross
            unitGross: paidAmount || 0, // ✅ Use paidAmount (deposit) as gross
            vatType: defaultVatType,
            vatExemptNote: defaultNote,
          }
        : {
            name: itemName,
            description: '',
            quantity: 1,
            unitNet: totalPrice, // ✅ Standard: use reservation totalPrice as net
            unitGross: 0, // Will be computed by InvoiceItemRow
            vatType: defaultVatType,
            vatExemptNote: defaultNote,
          };

      invoiceLogger.debug('[InvoiceItemsSection] 📦 Adding prefilled item:', itemToAdd);

      append(itemToAdd);
      prefilledItemNameRef.current = itemName;

      // Mark this reservation as applied
      appliedForReservationRef.current = reservationId;

      invoiceLogger.debug(
        '[InvoiceItemsSection] ✅ Prefill applied successfully. New fields count:',
        fields.length + 1
      );

      // Trigger VAT auto-suggestion after item is added
      if (onItemAdded) {
        invoiceLogger.debug(
          '[InvoiceItemsSection] Triggering onItemAdded callback for VAT suggestion'
        );
        // Use setTimeout to ensure item is fully added to form state
        setTimeout(() => onItemAdded(), 0);
      }
    } else {
      invoiceLogger.debug('[InvoiceItemsSection] ❌ Prefill skipped - conditions not met');
    }
  }, [
    reservationId,
    reservationDetails?.totalPrice,
    reservationDetails,
    fields.length,
    append,
    normalizedScheme,
    normalizedInvoiceType,
    isCorrectionInvoice,
    disableReservationPrefill,
    items,
    paidAmount,
    onItemAdded,
  ]);

  React.useEffect(() => {
    if (!reservationId || !reservationDetails || isCorrectionInvoice || disableReservationPrefill) {
      return;
    }

    if (appliedForReservationRef.current !== reservationId) {
      return;
    }

    const trackedPrefilledName = prefilledItemNameRef.current;
    if (!trackedPrefilledName) {
      return;
    }

    const currentItems = itemsRef.current;
    if (!Array.isArray(currentItems) || currentItems.length !== 1) {
      return;
    }

    const currentItemName = currentItems[0]?.name || '';
    if (currentItemName !== trackedPrefilledName) {
      prefilledItemNameRef.current = null;
      return;
    }

    const baseItemName = formatInvoiceItemName(reservationDetails);
    const refreshedItemName =
      normalizedInvoiceType === InvoiceType.Prepayment
        ? `Zaliczka - ${baseItemName}`
        : baseItemName;

    if (!refreshedItemName || refreshedItemName === currentItemName) {
      return;
    }

    setValue('items.0.name' as any, refreshedItemName, {
      shouldValidate: false,
      shouldDirty: false,
      shouldTouch: false,
    });
    prefilledItemNameRef.current = refreshedItemName;
  }, [
    reservationId,
    reservationDetails,
    isCorrectionInvoice,
    disableReservationPrefill,
    normalizedInvoiceType,
    setValue,
  ]);

  // ✅ Sync paidAmount → unitGross for Prepayment invoices
  React.useEffect(() => {
    const isPrepayment = normalizedInvoiceType === InvoiceType.Prepayment;

    if (!isPrepayment || paidAmount == null) {
      return;
    }

    invoiceLogger.debug('[InvoiceItemsSection] 🔄 Syncing paidAmount to unitGross:', {
      paidAmount,
      itemsCount: items.length,
      currentItems: items.map((i) => ({ name: i.name, unitGross: i.unitGross })),
    });

    if (items.length === 0) {
      invoiceLogger.debug('[InvoiceItemsSection] No items yet, skipping sync');
      return;
    }

    // Update unitGross for all items (usually there's only 1 item for Prepayment)
    items.forEach((item, index) => {
      const currentUnitGross = item.unitGross || 0;

      // Only update if different (avoid infinite loop)
      if (Math.abs(currentUnitGross - paidAmount) > 0.01) {
        invoiceLogger.debug(
          `[InvoiceItemsSection] ✏️ Updating item ${index} unitGross: ${currentUnitGross} → ${paidAmount}`
        );

        // ✅ OK to use update() here - this is triggered by external change (paidAmount), not user typing
        const updatedItem = { ...item, unitGross: paidAmount };
        update(index, updatedItem);
      } else {
        invoiceLogger.debug(
          `[InvoiceItemsSection] ✅ Item ${index} unitGross already correct: ${currentUnitGross}`
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidAmount, normalizedInvoiceType]); // ✅ Only depend on paidAmount and normalized invoice type, NOT on items/update

  // Handle item field change
  const handleItemChange = React.useCallback(
    (index: number, field: keyof CreateInvoiceItemForm, value: any) => {
      // ✅ Get current item state directly from form (not from watched items - prevents stale state)
      const currentItems = control._getWatch('items') || [];
      const watchedItem = currentItems[index];

      if (!watchedItem) {
        invoiceLogger.warn(`[InvoiceItemsSection] Item at index ${index} is undefined`);
        return;
      }

      // ✅ Track this update in pending map (for debugging/monitoring)
      const pendingUpdates = pendingUpdatesRef.current.get(index) || {};
      pendingUpdatesRef.current.set(index, { ...pendingUpdates, [field]: value });

      // ✅ CRITICAL: For computed fields (unitGross), we MUST use setValue
      // because they are NOT controlled by user input (no onChange event).
      // For user-input fields (name, description, unitNet), Controller handles updates automatically.
      //
      // ✅ EXCEPTION: vatType and vatExemptNote are user-controlled BUT we need to ensure RHF state is synced
      // when updated programmatically (e.g., from auto-fill logic)
      const computedFields: Array<keyof CreateInvoiceItemForm> = ['unitGross'];
      const controlledFieldsNeedingSync: Array<keyof CreateInvoiceItemForm> = [
        'vatType',
        'vatExemptNote',
      ];

      if (computedFields.includes(field) || controlledFieldsNeedingSync.includes(field)) {
        invoiceLogger.debug(
          `[InvoiceItemsSection] Setting field via setValue: items.${index}.${field} =`,
          value
        );
        setValue(`items.${index}.${field}` as any, value, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        });
      }

      // ✅ Clear pending updates after a short delay (when useWatch catches up)
      setTimeout(() => {
        pendingUpdatesRef.current.delete(index);
      }, 100);
    },
    [control, setValue] // ✅ Added setValue dependency
  );

  // Handle add new item
  // ✅ Wrap in useCallback for stable reference
  const handleAdd = React.useCallback(() => {
    // Determine vatType based on invoice type and scheme
    // Priority: Refund → VatNP (PFRON)
    //           TravelMargin scheme → VatMargin
    //           Default → Vat23
    const isRefund = normalizedInvoiceType === InvoiceType.Refund;
    const isTravelMargin = normalizedScheme === InvoiceScheme.TravelMargin;

    const defaultVatType = isRefund
      ? VatRateType.VatNP
      : isTravelMargin
        ? VatRateType.VatMargin
        : VatRateType.Vat23;

    // Get default note dynamically based on vatType and invoiceType
    const defaultNote = getDefaultVatExemptNote(defaultVatType, normalizedInvoiceType);

    append({
      name: '',
      description: '',
      quantity: 1,
      unitNet: 0,
      unitGross: 0, // Will be computed by InvoiceItemRow
      vatType: defaultVatType,
      vatExemptNote: defaultNote,
    } as CreateInvoiceItemForm);

    // Trigger VAT auto-suggestion after item is added manually
    if (onItemAdded) {
      invoiceLogger.debug('[InvoiceItemsSection] Manual item added, triggering VAT suggestion');
      // Use setTimeout to ensure item is fully added to form state
      setTimeout(() => onItemAdded(), 0);
    }
  }, [normalizedInvoiceType, normalizedScheme, append, onItemAdded]);

  // Handle delete item
  // ✅ Wrap in useCallback to prevent reference changes (causes child re-renders)
  const handleDelete = React.useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  // Compute error message
  const errorMessage = React.useMemo(() => {
    if (!errors.items) return null;
    if (typeof errors.items.message === 'string') {
      return errors.items.message;
    }
    if (Array.isArray(errors.items)) {
      const itemErrors = errors.items.filter((e) => e).length;
      return itemErrors > 0 ? `Błędy walidacji w ${itemErrors} pozycji` : null;
    }
    return 'Błędy w pozycjach faktury';
  }, [errors.items]);

  const liveGrossTotal = React.useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) {
      return 0;
    }

    return items.reduce((sum, item) => {
      const quantity = Number(item?.quantity ?? 0);
      const unitGross = Number(item?.unitGross ?? 0);
      return sum + quantity * unitGross;
    }, 0);
  }, [items]);

  return (
    <AccordionCard title="Pozycje" icon={<SettingsIcon color="primary" />}>
      <Box>
        {/* Render items using new InvoiceItemRow component (KROK 10) */}
        {fields.map((field, index) => (
          <InvoiceItemRow
            key={field.fieldKey}
            index={index}
            control={control}
            onItemChange={handleItemChange}
            onDelete={handleDelete}
            prevVatTypesRef={prevVatTypesRef}
          />
        ))}

        <Box sx={{ mt: 1, textAlign: 'right', color: 'text.secondary', fontSize: '0.875rem' }}>
          Suma brutto:{' '}
          <span data-testid="invoice-items-live-gross-total">{liveGrossTotal.toFixed(2)}</span>
        </Box>

        {/* Add new item button */}
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd} sx={{ mt: 2 }}>
          Dodaj pozycję
        </Button>

        {/* Error messages */}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Alert>
        )}
      </Box>
    </AccordionCard>
  );
};

export default InvoiceItemsSection;
